// components/AdminLogViewer.jsx
// Two-column display of bugs and enhancements (Qwizard-only)
import React, { useState, useEffect } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { updateLogEntry } from "../utils/adminLogs";

const STATUS_COLORS = {
  // Bugs
  current: "#ff6b6b",
  investigating: "#ffa94d",
  resolved: "#51cf66",
  // Enhancements
  planned: "#74c0fc",
  "in-progress": "#ffd43b",
  completed: "#51cf66",
};

const PRIORITY_EMOJI = {
  high: "🔴",
  medium: "🟡",
  low: "🟢",
};

function LogEntryCard({ entry, onEdit, onStatusToggle, onPriorityToggle }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = STATUS_COLORS[entry.status] || "#999";
  
  const handleStatusClick = async (e) => {
    e.stopPropagation(); // Prevent expanding the card
    if (onStatusToggle) {
      await onStatusToggle(entry);
    }
  };

  const handlePriorityClick = async (e) => {
    e.stopPropagation(); // Prevent expanding the card
    if (onPriorityToggle) {
      await onPriorityToggle(entry);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 12,
        padding: 8,
        marginBottom: 4,
        cursor: "pointer",
        transition: "background 0.2s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
      onClick={() => setExpanded(!expanded)}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            minWidth: 80,
            padding: "4px 8px",
            borderRadius: 6,
            background: statusColor,
            color: "#fff",
            fontWeight: 900,
            fontSize: "0.75em",
            textAlign: "center",
          }}
        >
          {entry.id}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ fontWeight: 700, color: "#fff", fontSize: "1.05em" }}>
              {entry.title}
            </div>
            <span
              onClick={handlePriorityClick}
              style={{
                fontSize: "1.2em",
                cursor: "pointer",
                transition: "transform 0.2s",
                display: "inline-block",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              title="Click to toggle priority (high → medium → low → high)"
            >
              {PRIORITY_EMOJI[entry.priority] || "⚪"}
            </span>
            <div
              onClick={handleStatusClick}
              style={{
                padding: "2px 8px",
                borderRadius: 4,
                background: statusColor,
                color: "#fff",
                fontSize: "0.75em",
                fontWeight: 700,
                cursor: "pointer",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              title="Click to toggle status"
            >
              {entry.status}
            </div>
          </div>
          {entry.tags && entry.tags.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: expanded ? 6 : 0 }}>
              {entry.tags.map((tag, i) => (
                <span
                  key={i}
                  style={{
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: "rgba(255,255,255,0.1)",
                    color: "#ccc",
                    fontSize: "0.7em",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          {expanded && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ fontSize: "0.85em", color: "#aaa", marginBottom: 6 }}>
                Created: {formatDate(entry.createdAt)}
                {entry.resolvedAt && ` • Resolved: ${formatDate(entry.resolvedAt)}`}
              </div>
              <div style={{ color: "#ddd", whiteSpace: "pre-wrap", marginBottom: 6 }}>
                {entry.description || "No description"}
              </div>
              {entry.resolvedInVersion && (
                <div style={{ fontSize: "0.85em", color: "#aaa", marginBottom: 6 }}>
                  Resolved in: <strong>{entry.resolvedInVersion}</strong>
                </div>
              )}
              {entry.notes && entry.notes.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: "#ccc" }}>Notes:</div>
                  {entry.notes.map((note, i) => (
                    <div
                      key={i}
                      style={{
                        padding: 6,
                        marginBottom: 4,
                        background: "rgba(0,0,0,0.2)",
                        borderRadius: 6,
                        fontSize: "0.85em",
                      }}
                    >
                      <div style={{ color: "#aaa", marginBottom: 4 }}>
                        {formatDate(note.timestamp?.toDate?.() || new Date())}
                        {note.statusChange && ` • Status: ${note.statusChange}`}
                      </div>
                      <div style={{ color: "#ddd" }}>{note.text}</div>
                    </div>
                  ))}
                </div>
              )}
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(entry);
                  }}
                  style={{
                    marginTop: 8,
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(71,227,115,0.2)",
                    color: "#47e373",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminLogViewer({ isQwizard, searchTerm: searchQuery = "", onEditEntry }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bugStatusFilter, setBugStatusFilter] = useState("current"); // Default: show current bugs
  const [enhancementStatusFilter, setEnhancementStatusFilter] = useState("planned"); // Default: show planned enhancements

  // Subscribe to real-time updates
  useEffect(() => {
    if (!isQwizard) return;

    const q = query(collection(db, "adminLogs"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const entries = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          entries.push({
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            resolvedAt: data.resolvedAt?.toDate?.() || null,
          });
        });
        setLogs(entries);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching logs:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isQwizard]);

  if (!isQwizard) {
    return null; // Don't show anything if not Qwizard
  }

  const normalizedSearch = (searchQuery || "").trim().toLowerCase();

  const bugs = logs.filter((l) => l.type === "bug");
  const enhancements = logs.filter((l) => l.type === "enhancement");

  const matchesSearch = (entry) => {
    if (!normalizedSearch) {
      return true;
    }

    const pieces = [
      entry.id,
      entry.title,
      entry.description,
      entry.priority,
      entry.status,
      ...(entry.tags || []),
      ...((entry.notes || []).flatMap((note) => [note.text, note.statusChange])),
    ]
      .filter(Boolean)
      .join(" | ")
      .toLowerCase();

    return pieces.includes(normalizedSearch);
  };

  // Priority sorting function: high first, then by date (newest first)
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const sortByPriorityThenDate = (a, b) => {
    // First sort by priority
    const priorityDiff = (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    // If priority is the same, sort by date (newest first)
    const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt || 0);
    const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt || 0);
    return dateB.getTime() - dateA.getTime(); // Descending (newest first)
  };

  const filteredBugs = bugs
    .filter((b) => b.status === bugStatusFilter)
    .filter(matchesSearch)
    .sort(sortByPriorityThenDate);
  const filteredEnhancements = enhancements
    .filter((e) => e.status === enhancementStatusFilter)
    .filter(matchesSearch)
    .sort(sortByPriorityThenDate);

  // Handle status toggle when clicking badge
  const handleStatusToggle = async (entry) => {
    try {
      let newStatus;
      if (entry.type === "bug") {
        // Toggle between "current" and "resolved"
        // If status is "current", change to "resolved", otherwise change to "current"
        newStatus = entry.status === "current" ? "resolved" : "current";
      } else {
        // Toggle between "planned" and "completed"
        // If status is "planned", change to "completed", otherwise change to "planned"
        newStatus = entry.status === "planned" ? "completed" : "planned";
      }
      
      await updateLogEntry(entry.id, { status: newStatus });
      
      // Add a note about the status change
      const { addLogNote } = await import("../utils/adminLogs");
      await addLogNote(entry.id, `Status changed from ${entry.status} to ${newStatus}`, `Status: ${entry.status} → ${newStatus}`);
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status: " + err.message);
    }
  };

  // Handle priority toggle when clicking traffic light emoji
  const handlePriorityToggle = async (entry) => {
    try {
      // Cycle through: high → medium → low → high
      let newPriority;
      if (entry.priority === "high") {
        newPriority = "medium";
      } else if (entry.priority === "medium") {
        newPriority = "low";
      } else {
        newPriority = "high";
      }
      
      await updateLogEntry(entry.id, { priority: newPriority });
      
      // Add a note about the priority change
      const { addLogNote } = await import("../utils/adminLogs");
      await addLogNote(entry.id, `Priority changed from ${entry.priority} to ${newPriority}`, `Priority: ${entry.priority} → ${newPriority}`);
    } catch (err) {
      console.error("Error updating priority:", err);
      alert("Failed to update priority: " + err.message);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: "center", color: "#fff" }}>Loading logs...</div>
    );
  }

  const handleEditEntry = onEditEntry ? (entry) => onEditEntry(entry) : null;

  return (
    <div style={{ marginTop: 40, marginBottom: 20 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          marginTop: 20,
        }}
      >
        {/* Bugs Column */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ color: "#ff6b6b", margin: 0, fontSize: "1.3em" }}>
              Bugs ({filteredBugs.length})
            </h3>
            <select
              value={bugStatusFilter}
              onChange={(e) => setBugStatusFilter(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(0,0,0,0.3)",
                color: "#fff",
                fontSize: "0.85em",
              }}
            >
              <option value="current">Current</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          {filteredBugs.length === 0 ? (
            <div style={{ color: "#aaa", fontStyle: "italic" }}>No bugs found</div>
          ) : (
            filteredBugs.map((entry) => (
              <LogEntryCard
                key={entry.id}
                entry={entry}
                onEdit={handleEditEntry}
                onStatusToggle={handleStatusToggle}
                onPriorityToggle={handlePriorityToggle}
              />
            ))
          )}
        </div>

        {/* Enhancements Column */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ color: "#74c0fc", margin: 0, fontSize: "1.3em" }}>
              Enhancements ({filteredEnhancements.length})
            </h3>
            <select
              value={enhancementStatusFilter}
              onChange={(e) => setEnhancementStatusFilter(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(0,0,0,0.3)",
                color: "#fff",
                fontSize: "0.85em",
              }}
            >
              <option value="planned">Planned</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          {filteredEnhancements.length === 0 ? (
            <div style={{ color: "#aaa", fontStyle: "italic" }}>No enhancements found</div>
          ) : (
            filteredEnhancements.map((entry) => (
              <LogEntryCard
                key={entry.id}
                entry={entry}
                onEdit={handleEditEntry}
                onStatusToggle={handleStatusToggle}
                onPriorityToggle={handlePriorityToggle}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
